import { useMutation, useQuery } from "@tanstack/react-query";
import { useHospitalData } from "../../../../../contexts/HospitalDataProvider";
import { useDebouncedValue } from "../../../../../hooks/useDebouncedValue";
import { backendService } from "../../../../../services/backend.service";
import type { AirtableFacilityOption, AirtablePreviewResponse, AirtableVerifyResponse } from "./airtableImportTypes";

export const AIRTABLE_IMPORT_QUERY_KEY = "airtable-config-import";

/** The endpoint's own `q` floor (`Query(min_length=2)`); a shorter query would be rejected, not searched. */
export const AIRTABLE_FACILITY_SEARCH_MIN_LENGTH = 2;
/** Long enough that a word typed at speed is one Airtable request, not one per letter. */
const AIRTABLE_FACILITY_SEARCH_DEBOUNCE_MS = 300;
/** Same reasoning as the listing's `staleTime`: the base changes over days, not minutes. */
const AIRTABLE_FACILITIES_STALE_MS = 5 * 60_000;

/** What the picker needs from the facilities endpoint: the whole candidate list, and whether it is whole. */
export type AirtableFacilities = {
	candidates: AirtableFacilityOption[];
	truncated: boolean;
};

/**
 * Step 1: EVERY implementation-intake record in the base, fetched once when the record step opens.
 *
 * One request, not one per keystroke. The base holds tens of records, and the endpoint is rate-limited
 * upstream (`airtable.import.rate.limited`) — so paging it per character was both slow and the thing
 * most likely to trip that limit, while making the operator wait a debounce for every letter. The
 * caller filters the loaded list instead, which is instant and cannot fail halfway through a word.
 *
 * Gated on `enabled` so merely opening the dialog does not reach out to Airtable.
 */
export function useAirtableFacilities({ enabled }: { enabled: boolean }) {
	return useQuery<AirtableFacilities>({
		queryKey: [AIRTABLE_IMPORT_QUERY_KEY, "facilities"],
		queryFn: async () => {
			const { data } = await backendService.getAirtableConfigFacilities();
			return { candidates: data.candidates, truncated: data.candidatesTruncated };
		},
		enabled,
		// Every failure this can return is an answer, not a blip: an expired PAT, a rate limit, or a
		// base we are not configured against will all fail again immediately.
		retry: false,
		// The base is edited by implementation staff over days, not minutes, and the whole point of one
		// up-front fetch is that reopening the wizard to check a second department costs nothing.
		staleTime: AIRTABLE_FACILITIES_STALE_MS,
	});
}

/**
 * Step 1, fallback: search the WHOLE base on Airtable's side, for when the up-front listing came back
 * truncated. Filtering a partial list in memory can never find a record past its end, so without this
 * the operator was told to go look in Airtable for a record the endpoint could have found itself.
 *
 * Only runs while `enabled` (the listing is truncated) and the query clears the endpoint's minimum, and
 * only on the debounced query — so a base that fits in one page never makes a second request at all.
 */
export function useAirtableFacilitySearch({ query, enabled }: { query: string; enabled: boolean }) {
	const debouncedQuery = useDebouncedValue(query.trim(), AIRTABLE_FACILITY_SEARCH_DEBOUNCE_MS);
	const searchable = enabled && debouncedQuery.length >= AIRTABLE_FACILITY_SEARCH_MIN_LENGTH;
	return useQuery<AirtableFacilities>({
		queryKey: [AIRTABLE_IMPORT_QUERY_KEY, "facilities", "search", debouncedQuery],
		queryFn: async () => {
			const { data } = await backendService.searchAirtableConfigFacilities(debouncedQuery);
			return { candidates: data.candidates, truncated: data.candidatesTruncated };
		},
		enabled: searchable,
		retry: false,
		staleTime: AIRTABLE_FACILITIES_STALE_MS,
	});
}

/**
 * Step 3 (read): the per-field diff for one record against THIS department.
 *
 * A mutation rather than a query even though it writes nothing: it must run exactly when the operator
 * advances to the review step, must never be replayed or refetched on window focus behind their back,
 * and the refusal path re-runs it explicitly to fetch the fresh diff.
 */
export function useAirtableConfigPreview() {
	const { chosenDepartment } = useHospitalData();
	return useMutation<AirtablePreviewResponse, Error, { airtableRecordId: string }>({
		mutationFn: async ({ airtableRecordId }) =>
			(await backendService.previewAirtableConfig({ departmentId: chosenDepartment.department_id, airtableRecordId })).data,
		retry: false,
	});
}

/**
 * The gate in front of Apply: re-read Airtable and confirm the record is still the revision the
 * operator reviewed. Refuses with `airtable.import.record.changed` when it is not — at which point the
 * caller re-previews and RESETS every accept checkbox, because carrying decisions over onto text nobody
 * read is exactly the rubber-stamping this flow exists to prevent.
 */
export function useVerifyAirtableConfig() {
	const { chosenDepartment } = useHospitalData();
	return useMutation<AirtableVerifyResponse, Error, { airtableRecordId: string; expectedFingerprint: string }>({
		mutationFn: async ({ airtableRecordId, expectedFingerprint }) =>
			(
				await backendService.verifyAirtableConfig({
					departmentId: chosenDepartment.department_id,
					airtableRecordId,
					expectedFingerprint,
				})
			).data,
		retry: false,
	});
}
